import { Injectable } from '@angular/core';
import { User } from '../models/User';
import { BackendService } from './backend.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

/**
 * User Data model
 */
export interface UserData {
  /**
   * Status
   */
  status: string;
  /**
   * Message
   */
  message: string;
  /**
   * Data
   */
  data: User;
}

export interface Company {
  _id: string;
  emailFormat: string;
  title: string;
  activateAchievements: boolean;
  activateAchievementsLimit: boolean;
  branchoffices: Branchoffice[];
}

export interface Branchoffice {
  _id: string;
  title: string;
}

/**
 * User service
 */
@Injectable()
export class UserService {
  public currentUser: BehaviorSubject<User> = new BehaviorSubject(null);

  private _user: User;

  /**
   * Constructor
   * @param {BackendService} backendService
   */
  constructor(private backendService: BackendService) {
    this.currentUser.next(JSON.parse(localStorage.getItem('user')));
  }

  /**
   * Stores a User object as the current user to be used and displayed around the app
   * @param {User} user The User object to store
   */
  private setCurrentUser(user: User) {
    this.currentUser.next(user);
    this._user = user;
    localStorage.setItem('user', JSON.stringify(user));
  }

  /**
   * Creates new user
   * @param {string} prename Prename
   * @param {string} surname Surname
   * @param {string} email Email
   * @param {string} password Password
   * @returns {Promise<any>} Response as Promise
   */
  createUser(prename: string, surname: string, email: string, password: string) {
    return this.backendService.post('user', {
      prename: prename,
      surname: surname,
      email: email,
      password: password,
    });
  }

  /**
   * Updates user object
   * @param firstName
   * @param lastName
   * @param gender
   * @param language
   * @param timeZone
   * @param alias
   */
  updateUser(user: any) {
    return this.backendService.put('user/me', user);
  }

  /**
   * Activates user
   * @param {number} userId User ID
   * @param {string} token Activate-Token
   * @returns {Promise<any>} Response as Promise
   */
  activateUser(userId: number, token: string): Promise<any> {
    return this.backendService.post('user/' + userId + '/activate/' + token);
  }

  getThisUser() {
    return this.backendService.get('user/me').then((result: User) => {
      this.setCurrentUser(result);
    });
  }

  /**
   * Sends a request to reset password
   * @param {string} email Email
   * @returns {Promise<any>} Response as Promise
   */
  requestPasswordReset(email: string) {
    return this.backendService.post('auth/forgotpass', {
      email: email,
    });
  }

  /**
   * Sends new password after reset request
   * @param email Email
   * @param password Password
   * @param token Redeem password reset token
   * @returns {Promise<any>} Response as Promise
   */
  redeemPasswordReset(password, token) {
    return this.backendService.post('auth/forgotpass/new/' + token, {
      password: password,
    });
  }

  /**
   * Set a new Password for this user
   * @param oldPassword
   * @param newPassword
   */
  setPassword(oldPassword, newPassword) {
    return this.backendService.post('auth/changepass', {
      oldPassword: oldPassword,
      newPassword: newPassword,
    });
  }

  /**
   * Reactivate inactive user
   * @param token
   */
  reactivateUser(token) {
    return this.backendService.get(`user/inactivity/${token}/reactivate`);
  }

  /**
   * Delete inactive user
   * @param token
   */
  deleteUser(token) {
    return this.backendService.get(`user/inactivity/${token}/delete`);
  }

  getCompany(): Promise<Company> {
    return this.backendService.get(`companies/`);
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/shared/services/user.service.ts